import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Group,
  Paper,
  Select,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Bookmark,
  CircleHelp,
  Plus,
  RefreshCw,
  Search,
  Wrench,
} from 'lucide-react';
import type { MouseEvent } from 'react';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  forwardRef,
} from 'react';
import { useAdminResourceListQuery } from '../../hooks/queries/useAdminResourceListQuery';
import { ResourceModal } from './Resource.modal';
import { TableLoadingSkeleton } from './TableLoadingSkeleton';

const RESOURCE_NAME = 'course' as const;
const PAGE_SIZES = [10, 15, 20] as const;

export type AdminCourseRow = Record<string, unknown> & {
  id?: string | number;
};

export type AdminCoursesTableHandle = {
  setSelectedRecord: (record: AdminCourseRow) => void;
};

const courseColumnHelper = createColumnHelper<AdminCourseRow>();

export const AdminCoursesTable = forwardRef<AdminCoursesTableHandle>(
  function AdminCoursesTable(_props, ref) {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[1]);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchValue, 400);
    const [selectedRecord, setSelectedRecord] = useState<AdminCourseRow>();
    const [opened, { open, close }] = useDisclosure(false);

    const { data, isLoading, isFetching, error, refetch } =
      useAdminResourceListQuery(RESOURCE_NAME, page, pageSize, debouncedSearch);

    useImperativeHandle(ref, () => ({
      setSelectedRecord: (record: AdminCourseRow) => {
        setSelectedRecord(record);
      },
    }));

    useEffect(() => {
      if (selectedRecord != null) {
        open();
      }
    }, [selectedRecord, open]);

    useEffect(() => {
      if (!opened) {
        setSelectedRecord(undefined);
      }
    }, [opened]);

    useEffect(() => {
      setPage(1);
    }, [debouncedSearch]);

    const rows = useMemo(() => {
      const r = data?.rows;
      return Array.isArray(r) ? (r as AdminCourseRow[]) : [];
    }, [data?.rows]);

    const totalRecords = data?.totalRecords ?? 0;
    const totalPages = data?.totalPages ?? 1;

    const invalidateList = useCallback(() => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'resource', RESOURCE_NAME, 'list'],
      });
    }, [queryClient]);

    const onModalSave = useCallback(() => {
      invalidateList();
      close();
    }, [close, invalidateList]);

    const columns = useMemo(
      () => [
        courseColumnHelper.accessor('id', {
          header: 'ID',
          cell: info => String(info.getValue() ?? ''),
        }),
        courseColumnHelper.accessor('name', {
          header: 'Name',
          cell: info => String(info.getValue() ?? ''),
        }),
        courseColumnHelper.accessor('product_name', {
          header: 'Product',
          cell: info => String(info.getValue() ?? ''),
        }),
        courseColumnHelper.accessor('url', {
          header: 'Url',
          cell: info => String(info.getValue() ?? ''),
        }),
        courseColumnHelper.accessor('materials', {
          header: 'Materials',
          cell: info => {
            const v = info.getValue();
            if (v == null) return '';
            if (typeof v === 'object') {
              return JSON.stringify(v);
            }
            return String(v);
          },
        }),
        courseColumnHelper.display({
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => {
            const i = row.original;
            return (
              <Box
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                }}
              >
                <ButtonGroup>
                  <Tooltip label='Edit'>
                    <Button
                      color='gray'
                      onClick={() => setSelectedRecord(i)}
                      variant='outline'
                      size='compact-md'
                    >
                      <Wrench size={16} />
                    </Button>
                  </Tooltip>
                  <Tooltip label='Edit chapters'>
                    <Button
                      color='gray'
                      onClick={() =>
                        window.open(
                          `/admin/courseChapter?course_id=${String(i.id)}`,
                          '_blank'
                        )
                      }
                      variant='outline'
                      size='compact-md'
                    >
                      <Bookmark size={16} />
                    </Button>
                  </Tooltip>
                  <Tooltip label='Edit Questions'>
                    <Button
                      color='gray'
                      variant='outline'
                      onClick={() =>
                        window.open(
                          `/admin/courseQuestions?course_id=${String(i.id)}`,
                          '_blank'
                        )
                      }
                      size='compact-md'
                    >
                      <CircleHelp size={16} />
                    </Button>
                  </Tooltip>
                </ButtonGroup>
              </Box>
            );
          },
        }),
      ],
      [setSelectedRecord]
    );

    const table = useReactTable({
      data: rows,
      columns,
      getCoreRowModel: getCoreRowModel(),
    });

    const handleRowClick = useCallback(
      (record: AdminCourseRow) => {
        setSelectedRecord(record);
      },
      [setSelectedRecord]
    );

    const startRecord = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
    const endRecord = Math.min(page * pageSize, totalRecords);

    if (isLoading && rows.length === 0) {
      return <TableLoadingSkeleton rows={5} columns={6} />;
    }

    if (error) {
      return (
        <Paper p='md' withBorder>
          <Text c='red' mb='md'>
            Error loading courses:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </Text>
          <Button
            onClick={() => void refetch()}
            leftSection={<RefreshCw size={16} />}
          >
            Retry
          </Button>
        </Paper>
      );
    }

    const columnCount = columns.length;

    return (
      <Box>
        <ResourceModal
          opened={opened}
          close={close}
          name={RESOURCE_NAME}
          callback={onModalSave}
          selectedRecord={selectedRecord}
        />

        <Paper p='md' withBorder>
          <Flex
            justify='space-between'
            align='center'
            mb='md'
            gap='md'
            wrap='wrap'
          >
            <TextInput
              placeholder='Search...'
              leftSection={<Search size={16} />}
              flex={1}
              miw={200}
              value={searchValue}
              onChange={event => setSearchValue(event.currentTarget.value)}
            />
            <Group gap='xs' wrap='nowrap'>
              <Button
                variant='light'
                size='sm'
                onClick={() => void refetch()}
                loading={isFetching}
                leftSection={<RefreshCw size={16} />}
              >
                Refresh
              </Button>
              <Button
                variant='light'
                size='sm'
                onClick={() => {
                  setSelectedRecord(undefined);
                  open();
                }}
                leftSection={<Plus size={16} />}
              >
                Add
              </Button>
            </Group>
          </Flex>

          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              {table.getHeaderGroups().map(headerGroup => (
                <Table.Tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <Table.Th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Table.Th>
                  ))}
                </Table.Tr>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {isFetching && rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={columnCount}
                    style={{ textAlign: 'center' }}
                  >
                    <Text size='sm' c='dimmed'>
                      Loading...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={columnCount}
                    style={{ textAlign: 'center' }}
                  >
                    <Text size='sm' c='dimmed'>
                      No records found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <Table.Tr
                    key={row.id}
                    onClick={() => handleRowClick(row.original)}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <Table.Td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>

          <Flex
            justify='space-between'
            align='center'
            mt='md'
            gap='md'
            wrap='wrap'
          >
            <Group gap='xs'>
              <Text size='sm'>Records per page:</Text>
              <Select
                value={String(pageSize)}
                onChange={v => {
                  if (v) {
                    setPageSize(Number(v));
                    setPage(1);
                  }
                }}
                data={PAGE_SIZES.map(size => ({
                  value: String(size),
                  label: String(size),
                }))}
                w={80}
              />
            </Group>

            <Text size='sm' c='dimmed'>
              Showing {startRecord} to {endRecord} of {totalRecords} records
            </Text>

            <Group gap='xs'>
              <Button
                variant='light'
                size='sm'
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Text size='sm'>
                Page {page} of {totalPages}
              </Text>
              <Button
                variant='light'
                size='sm'
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages || totalRecords === 0}
              >
                Next
              </Button>
            </Group>
          </Flex>
        </Paper>
      </Box>
    );
  }
);
